<template>
  <Notification :item="item">
    <template #logo>
      <BittsAvatar :org="partnerOrg" :show-initials="true" size="x-small" />
    </template>
    <template #message> Partnership Declined </template>
    <template #description>
      {{ userName }} at {{ organizationName }} declined your partnership
      request.
    </template>
    <template #action>
      <BittsButton
        text="View Reason"
        type="neutral"
        variant="outline"
        size="x-small"
        @click="openReason"
      />
    </template>
    <template #icon>
      <FontAwesomeIcon
        :icon="['fak', 'team']"
        :style="{ height: '12px', width: '12px', color: 'currentColor' }"
        class="text-neutral-accent ml-4"
      />
    </template>
  </Notification>
</template>
<script setup lang="ts">
import { BittsAvatar, BittsButton } from '@crossbeam/bitts';

import { computed } from 'vue';
import { useRouter } from 'vue-router';

import Notification from '@/components/notifications/Notification.vue';

import { useNotificationsStore, usePartnersStore } from '@/stores';
import { Notification as NotificationType } from '@/types/notifications';

const { item } = defineProps<{ item: NotificationType }>();

const emit = defineEmits<(e: 'action-clicked', action: string) => void>();

const userName = computed(() => item.data?.decliner_user_name);
const organizationName = computed(() => item.data?.decliner_org_name);

const partnersStore = usePartnersStore();
// A declined org is usually not an established partner, so fall back to a
// name-only object so the avatar can still render initials.
const partnerOrg = computed(
  () =>
    partnersStore.getPartnerOrgById(item.data?.decliner_org_id || 0) ?? {
      name: organizationName.value,
    },
);

const router = useRouter();
const notificationsStore = useNotificationsStore();
// Deep-link into the Requests drawer (Sent tab, Partnership filter) and let it
// auto-open the reason modal for this proposal — reusing the real host so the
// modal's "Invite again" flow works. object_id is the declined proposal id.
async function openReason() {
  emit('action-clicked', 'View Reason');
  await notificationsStore.markRead({ id: item.id, isRead: true });
  await router.push({
    name: 'partner_wishlist_requests',
    query: {
      tab: 'sent',
      view: 'partnerships',
      view_reason: String(item.object_id),
    },
  });
}
</script>
